import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import AvatarChip from '../../../../shared/avatar-chip';
import {
  generalFieldMergeTags,
  triggerEventOptions,
} from '../trigger-event-options';
import { Editor } from 'tinymce';
import DistractionFreeEditor from '../../../../shared/editor/distraction-free-editor';
import MergeTagOnlyEditor from '../../../../shared/editor/mergetag-only-editor';
import ActionDescription from './action-description';
import { RulesEngineFormValues } from '../rules-engine-form-page';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface SendEmailActionProps {
  description?: string;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  control: Control<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  trigger: UseFormTrigger<RulesEngineFormValues>;
  clearErrors: UseFormClearErrors<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
}

export default function SendEmailAction({
  description,
  setValue,
  control,
  watch,
  trigger,
  errors,
  clearErrors,
}: SendEmailActionProps) {
  const emailNotificatinKey = useRef(new Date().getTime().toString());
  const [addedEmail, setAddedEmail] = useState('');

  const emailNotificationEmails = watch('emailInfo.emailNotificationEmails');

  const htmlBody = watch('emailInfo.emailMessage');
  const emailSubject = watch('emailInfo.emailSubject');
  const triggerEvent = watch('triggerInfo.triggerEvent');

  const currentTriggerEvent = triggerEventOptions.find(
    (option) => option.value === triggerEvent
  );

  const editorRef = useRef<Editor | null>(null);
  const editorSubjectRef = useRef<Editor | null>(null);

  function setHtmlBody() {
    if (editorRef.current) {
      setValue('emailInfo.emailMessage', editorRef.current.getContent(), {
        shouldDirty: true,
      });
    }
  }

  function setSubject() {
    if (editorSubjectRef.current) {
      setValue(
        'emailInfo.emailSubject',
        editorSubjectRef.current.getContent(),
        {
          shouldDirty: true,
        }
      );
    }
  }

  const mergeTags = [
    generalFieldMergeTags,
    {
      title: 'Event Specific Fields',
      menu: currentTriggerEvent?.availableParameters.map((param) => {
        return {
          value: param.value,
          title: param.displayValue,
        };
      }),
    },
  ];

  return (
    <Stack gap={1} width={'100%'}>
      <ActionDescription description={description} />
      <Stack flexDirection={'row'} gap={1}>
        <Controller
          name={'emailInfo.emailNotificationEmails'}
          control={control}
          render={({ field }) => (
            <Box sx={{ width: 400 }}>
              <SharedAutocomplete
                sources={['users', 'groups']}
                autocompleteValue={addedEmail}
                setAutoCompleteValue={(val) => {
                  setAddedEmail(val);
                  if (val && !field.value?.includes(val)) {
                    const updatedUsers = [...(field.value || []), val];
                    setValue(
                      'emailInfo.emailNotificationEmails',
                      updatedUsers,
                      {
                        shouldDirty: true,
                      }
                    );
                    setAddedEmail('');
                    emailNotificatinKey.current = new Date()
                      .getTime()
                      .toString();
                    trigger('emailInfo.emailNotificationEmails');
                    clearErrors('emailInfo.emailNotificationEmails');
                  }
                }}
                errors={!!errors.emailInfo?.emailNotificationEmails}
                helperText={
                  errors?.emailInfo?.emailNotificationEmails?.message || ''
                }
              />
            </Box>
          )}
        />
      </Stack>
      <Stack
        flexDirection={'row'}
        gap={1}
        maxWidth={'100%'}
        sx={{ overflowX: 'auto' }}
      >
        {emailNotificationEmails.length ? (
          emailNotificationEmails.map((user) => {
            return (
              <Box key={user}>
                <AvatarChip
                  email={user}
                  type={'Unknown'}
                  onDelete={() => {
                    setValue(
                      'emailInfo.emailNotificationEmails',
                      emailNotificationEmails.filter((email) => email !== user)
                    );
                  }}
                />
              </Box>
            );
          })
        ) : (
          <Box height={25} />
        )}
      </Stack>
      <Typography sx={{ fontWeight: 'bold' }}>Subject</Typography>
      <Box sx={{ height: 110 }}>
        <MergeTagOnlyEditor
          editorRef={editorSubjectRef}
          content={emailSubject}
          setContent={setSubject}
          placeholder="Type your email subject here..."
          mergeTags={mergeTags}
        />
      </Box>
      <Typography sx={{ fontWeight: 'bold' }}>Body</Typography>
      <Box sx={{ height: 200 }}>
        <DistractionFreeEditor
          editorRef={editorRef}
          content={htmlBody}
          setContent={setHtmlBody}
          placeholder="Type your email body here..."
          mergeTags={mergeTags}
        />
      </Box>
    </Stack>
  );
}
